import React from "react";
import { Link } from "react-router-dom";

export const TermsAndReturns = () => {
  return (
    <div className="bg-ink text-bone min-h-screen pt-32 pb-20 px-5 md:px-14 lg:px-20 font-serif">
      <div className="max-w-3xl mx-auto space-y-12">
        <header className="space-y-4 border-b border-bone/10 pb-8">
          <p className="label text-stone tracking-[0.25em] text-xs uppercase">Client Care & Policies</p>
          <h1 className="text-4xl md:text-5xl font-light text-bone">Terms of Sale & Returns</h1>
        </header>

        <section className="space-y-10 text-stone font-sans text-sm leading-relaxed font-light">
          {/* 1. Merchant & Brand Information */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">1. Merchant & Brand Information</h2>
            <p>
              This e-commerce platform operates under the brand <strong className="text-bone">ALKEON Timeless Design</strong>, managed by:
            </p>
            <p className="mt-2 text-bone">
              PS3D CREATIVE di Sampaolesi Pierpaolo<br />
              Registered Office: Recanati (MC), Italy<br />
              VAT ID / P.IVA: IT02117040432<br />
              Contact Email:{" "}
              <a href="mailto:info@alkeontimelessdesign.com" className="text-bone underline underline-offset-4 hover:text-sand transition-colors">
                info@alkeontimelessdesign.com
              </a>
            </p>
          </div>

          {/* 2. Atelier Craftsmanship */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">2. Atelier Craftsmanship & Material Variations</h2>
            <p>
              Every creation by ALKEON Timeless Design is individually hand-cast and finished in our atelier. Subtle surface textures, micro-porosities, organic shade nuances, and mineral variations are inherent characteristics of our composite stone material, Jesmonite, and our artisanal processes.
            </p>
            <p className="mt-2">
              These characteristics reflect the handmade nature of each creation and do not, in themselves, constitute manufacturing defects or a lack of conformity, without prejudice to the consumer's statutory rights. Each piece is therefore unique, with variations that contribute to its individual character and material authenticity.
            </p>
          </div>

          {/* 3. Orders & Production Times */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">3. Orders, Availability & Production Times</h2>
            <p>
              To maintain our standards of quality, selected creations are produced in limited atelier editions or prepared upon request. Estimated production and dispatch times are communicated on the relevant product page or during the ordering process. Should any unexpected delay arise, we will inform the customer promptly and provide updated information. An order becomes binding upon confirmation by the seller, subject to the applicable terms of sale.
            </p>
          </div>

          {/* 4. Prices, Currency & Tax Regime */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">4. Prices, Currency & Customs Duties</h2>
            <p>
              All prices are displayed in Euros (€). VAT is not applied under the Italian flat-rate tax regime (Regime Forfettario), pursuant to Italian Law No. 190/2014, as amended.
            </p>
            <p className="mt-2">
              Any applicable shipping costs are clearly displayed before the customer completes the purchase. For deliveries outside the European Union, additional import taxes, customs duties, or clearance fees may apply. Unless expressly stated otherwise, such charges are not included in the purchase price and are the responsibility of the recipient. International orders may be subject to specific tax and customs requirements depending on the destination country.
            </p>
          </div>

          {/* 5. Payments & Confirmation */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">5. Payments & Order Confirmation</h2>
            <p>
              Available payment methods are displayed during checkout. Payment must be completed using one of the methods offered on the website. Customers will receive an order confirmation by email once their order has been accepted. If an order cannot be fulfilled, the customer will be informed and any payment received for the unfulfilled order will be refunded in accordance with applicable law.
            </p>
          </div>

          {/* 6. Shipping & Delivery */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">6. Shipping & Delivery</h2>
            <p>
              Orders are shipped using suitable transport partners and protective packaging designed to help safeguard each creation during transit. Available shipping destinations, applicable shipping costs, and estimated delivery times are communicated during the purchasing process.
            </p>
            <p className="mt-2">
              Upon delivery, customers are encouraged to inspect the external packaging. If visible damage is present, we recommend noting the condition of the parcel on the carrier's delivery receipt, where possible, and contacting us promptly at{" "}
              <a href="mailto:info@alkeontimelessdesign.com" className="text-bone underline underline-offset-4 hover:text-sand transition-colors">
                info@alkeontimelessdesign.com
              </a>. Prompt notification helps us investigate transit damage and provide appropriate assistance. However, failure to report damage within a specific period does not affect any mandatory rights available to the consumer under applicable law.
            </p>
          </div>

          {/* 7. Right of Withdrawal & Return Policy */}
          <div className="space-y-4">
            <h2 className="text-bone font-serif text-lg mb-2">7. Right of Withdrawal & Return Policy</h2>
            <p>
              In accordance with applicable European Union and Italian consumer protection legislation, consumers generally have the right to withdraw from a distance purchase without providing a reason within 14 calendar days of receiving the goods.
            </p>

            <div className="pl-4 border-l border-bone/10 space-y-3 mt-4">
              <h3 className="text-bone font-serif text-base">7.1 How to Exercise the Right of Withdrawal</h3>
              <p>
                To exercise the right of withdrawal, customers must inform us of their decision through an unequivocal written statement sent to{" "}
                <a href="mailto:info@alkeontimelessdesign.com" className="text-bone underline underline-offset-4 hover:text-sand transition-colors">
                  info@alkeontimelessdesign.com
                </a>{" "}
                within 14 calendar days of receiving the goods. Customers may use the model withdrawal form provided below, although its use is not mandatory.
              </p>

              <h3 className="text-bone font-serif text-base pt-2">7.2 Returning the Goods</h3>
              <p>
                Following communication of the withdrawal, customers must send the goods back within 14 calendar days. Direct return shipping costs are borne by the customer. We recommend using suitable protective packaging and a tracked shipping service to reduce the risk of damage or loss during transit.
              </p>

              <h3 className="text-bone font-serif text-base pt-2">7.3 Condition of Returned Items</h3>
              <p>
                Customers may handle and inspect products to the extent necessary to establish their nature, characteristics, and functioning. Customers may be held responsible for any diminished value resulting from handling beyond what is necessary. Whenever possible, products should be returned with their original protective packaging. The absence of original packaging does not automatically invalidate the right of withdrawal.
              </p>

              <h3 className="text-bone font-serif text-base pt-2">7.4 Refunds</h3>
              <p>
                When a valid withdrawal is exercised, we will reimburse payments received from the customer, including original standard delivery costs where required by law. Refunds will be issued without undue delay and within 14 calendar days of receiving notification. However, we may withhold reimbursement until we have received the returned goods or proof of return dispatch.
              </p>

              <h3 className="text-bone font-serif text-base pt-2">7.5 Exceptions for Bespoke & Personalized Creations</h3>
              <p>
                The right of withdrawal does not apply to goods made to the consumer's specifications or clearly personalized. The mere fact that an item is produced to order from our catalog does not automatically exclude the right of withdrawal.
              </p>

              {/* 7.6 Model Form */}
              <div className="bg-bone/5 p-6 rounded-sm mt-4 font-mono text-xs text-bone/80 space-y-2 border border-bone/10">
                <p className="font-serif text-bone text-sm mb-2">7.6 Model Withdrawal Form</p>
                <p>To: PS3D CREATIVE di Sampaolesi Pierpaolo</p>
                <p>Email: info@alkeontimelessdesign.com</p>
                <p className="pt-2">I hereby give notice that I withdraw from my contract of sale for the following goods:</p>
                <ul className="list-disc pl-5 space-y-1 pt-1">
                  <li>Product name:</li>
                  <li>Order number:</li>
                  <li>Date of order / Date of receipt:</li>
                  <li>Customer name & address:</li>
                  <li>Date:</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 8. Legal Warranty */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">8. Legal Warranty of Conformity</h2>
            <p>
              Products sold to consumers are covered by the applicable statutory legal warranty of conformity (2 years for EU consumers from delivery). If a product presents a genuine lack of conformity not related to the natural material variations described in Section 2, consumers are entitled to statutory remedies including repair, replacement, or refund.
            </p>
          </div>

          {/* 9. Customer Care */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">9. Customer Care & Assistance</h2>
            <p>
              For assistance with orders, shipping, material characteristics, or bespoke inquiries, contact our concierge service directly at{" "}
              <a href="mailto:info@alkeontimelessdesign.com" className="text-bone underline underline-offset-4 hover:text-sand transition-colors">
                info@alkeontimelessdesign.com
              </a>.
            </p>
          </div>

          {/* 10. Governing Law */}
          <div>
            <h2 className="text-bone font-serif text-lg mb-2">10. Governing Law & Jurisdiction</h2>
            <p>
              These terms are governed by Italian law, without prejudice to mandatory consumer protection provisions applicable in the customer's country of habitual residence. Any disputes will be handled by the competent courts in accordance with applicable law.
            </p>
          </div>
        </section>

        <div className="pt-8 border-t border-bone/10">
          <Link to="/" className="label text-xs tracking-widest text-stone hover:text-bone transition-colors uppercase">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsAndReturns;